import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, BookOpen, Tag } from 'lucide-react';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useDocumentMeta } from '../lib/useDocumentMeta';

const API = (() => {
  if (typeof window !== 'undefined') {
    const host = window.location.hostname;
    if (host === 'smt-services17.fr' || host === 'www.smt-services17.fr' || host.endsWith('.emergent.host')) {
      return `${window.location.origin}/api`;
    }
  }
  return `${process.env.REACT_APP_BACKEND_URL || ''}/api`;
})();

const CATEGORY_META = {
  vitres:    { label: 'Vitres',        color: 'bg-blue-100 text-blue-700' },
  terrasse:  { label: 'Terrasse',      color: 'bg-emerald-100 text-emerald-700' },
  menage:    { label: 'Ménage',        color: 'bg-purple-100 text-purple-700' },
  moquette:  { label: 'Moquette',      color: 'bg-amber-100 text-amber-700' },
  veranda:   { label: 'Véranda',       color: 'bg-sky-100 text-sky-700' },
  local:     { label: 'SEO local',     color: 'bg-rose-100 text-rose-700' },
  general:   { label: 'Général',       color: 'bg-slate-100 text-slate-700' },
};

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
  } catch { return iso; }
};

const BlogCard = ({ post }) => {
  const meta = CATEGORY_META[post.category] || CATEGORY_META.general;
  return (
    <Link
      to={`/blog/${post.slug}`}
      data-testid={`blog-card-${post.slug}`}
      className="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-2xl transition-all duration-500 hover:-translate-y-1 border border-slate-100"
    >
      <div className="relative aspect-[16/10] overflow-hidden bg-slate-100">
        <img
          src={post.cover_image}
          alt={post.title}
          loading="lazy"
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
        />
        <span className={`absolute top-4 left-4 text-[11px] font-bold uppercase tracking-widest px-3 py-1.5 rounded-full shadow ${meta.color}`}>
          {meta.label}
        </span>
      </div>
      <div className="p-6">
        <p className="text-xs text-slate-400 font-medium mb-2">{formatDate(post.published_at)}</p>
        <h3 className="text-lg sm:text-xl font-bold text-slate-900 mb-2 leading-snug line-clamp-2 group-hover:text-[hsl(207,90%,54%)] transition-colors">
          {post.title}
        </h3>
        <p className="text-sm text-slate-600 line-clamp-3 leading-relaxed">
          {post.excerpt}
        </p>
        <p className="mt-4 text-sm font-semibold text-[hsl(207,90%,54%)] inline-flex items-center gap-1.5 group-hover:gap-2.5 transition-all">
          Lire l'article <ArrowRight className="w-4 h-4" />
        </p>
      </div>
    </Link>
  );
};

const BlogList = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState('all');
  const navigate = useNavigate();

  useDocumentMeta({
    title: 'Blog SMT Services — Conseils & guides nettoyage en Charente-Maritime',
    description:
      "Astuces de pros, guides pratiques et conseils d'experts pour entretenir vitres, terrasses, moquettes et canapés. Conseils par les artisans nettoyeurs de Royan & Chaillevette.",
    path: '/blog',
    ogType: 'website',
    jsonLd: posts.length > 0 ? {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name: 'Blog SMT Services',
      itemListElement: posts.slice(0, 10).map((p, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        url: `https://smt-services17.fr/blog/${p.slug}`,
        name: p.title,
      })),
    } : null,
  });

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/blog`, { params: category !== 'all' ? { category } : {} })
      .then((res) => setPosts(res.data))
      .catch(() => setPosts([]))
      .finally(() => setLoading(false));
  }, [category]);

  // Collect available categories for filter
  const availableCategories = Array.from(new Set(posts.map((p) => p.category))).filter(Boolean);
  const filterOptions = [
    { id: 'all', label: 'Tous' },
    ...Object.keys(CATEGORY_META).filter((k) => availableCategories.includes(k) || category === k)
      .map((k) => ({ id: k, label: CATEGORY_META[k].label })),
  ];

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <Header />
      <main className="flex-1">
        {/* Hero */}
        <section className="relative bg-gradient-to-br from-[hsl(207,90%,54%)] via-[hsl(207,90%,44%)] to-[hsl(220,90%,30%)] text-white overflow-hidden pt-32 pb-20">
          <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle at 25% 20%, white 0%, transparent 50%)' }} />
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
            <div className="inline-flex items-center gap-2 bg-white/15 backdrop-blur px-3 py-1 rounded-full text-xs font-bold uppercase tracking-widest mb-4">
              <BookOpen className="w-3.5 h-3.5" />
              Conseils d'expert
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-5">
              Blog SMT Services
            </h1>
            <p className="text-lg sm:text-xl text-white/85 max-w-2xl mx-auto">
              Astuces, guides et méthodes de professionnels pour entretenir votre maison et prolonger la vie de vos biens.
            </p>
          </div>
        </section>

        {/* Filters */}
        <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 -mt-8 relative z-20 mb-10">
          <div className="bg-white rounded-2xl shadow-lg p-4 flex flex-wrap gap-2 justify-center">
            {filterOptions.map((opt) => (
              <button
                key={opt.id}
                data-testid={`blog-filter-${opt.id}`}
                onClick={() => setCategory(opt.id)}
                className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${
                  category === opt.id
                    ? 'bg-[hsl(207,90%,54%)] text-white shadow'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </section>

        {/* Posts grid */}
        <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
          {loading ? (
            <div className="text-center py-20 text-slate-400">Chargement…</div>
          ) : posts.length === 0 ? (
            <div className="text-center py-20">
              <Tag className="w-10 h-10 mx-auto mb-3 text-slate-300" />
              <p className="text-slate-500">Aucun article pour le moment dans cette catégorie.</p>
              <button
                onClick={() => setCategory('all')}
                className="mt-4 text-[hsl(207,90%,54%)] font-semibold"
              >
                Voir tous les articles
              </button>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
              {posts.map((p) => <BlogCard key={p.id} post={p} />)}
            </div>
          )}
        </section>

        {/* CTA */}
        <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
          <div className="bg-gradient-to-br from-[hsl(32,95%,55%)] to-[hsl(32,95%,45%)] rounded-3xl p-10 text-center text-white shadow-xl">
            <h2 className="text-2xl sm:text-3xl font-bold mb-3">Besoin d'une intervention professionnelle ?</h2>
            <p className="opacity-90 mb-6 max-w-xl mx-auto">
              Nos équipes interviennent dans un rayon de 25 km autour de Chaillevette pour un rendu impeccable et durable.
            </p>
            <button
              onClick={() => navigate('/#reservation')}
              className="bg-white text-[hsl(32,95%,45%)] font-bold px-8 py-3 rounded-full shadow-lg hover:shadow-2xl transition-all hover:-translate-y-0.5"
            >
              Demander un devis gratuit
            </button>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default BlogList;
